'use client'

import { useEffect, useMemo, useState } from 'react'
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton'
import FaqItem from '@/app/faq/_components/FaqItem'
import { useTranslation } from 'react-i18next'
import { GA_EVENTS } from '@/utils/analytics'
import { useFaqDataQuery } from '@/hooks/useContentfulQuery'
import { Button } from '@peacecover/component'
import { FAQ_CATEGORIES } from '@/constants/faq'
import { useLang } from '@/store/useLanguageStore'
import ContactCard from './ContactCard'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

const Faq = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('FAQ')

  const { t } = useTranslation('faq')
  const { lang } = useLang()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const getFaqContentLang = (lang: string) => {
    switch (lang) {
      case 'ko':
        return 'faq'
      case 'en':
        return 'faqEn'
      case 'zh-CN':
        return 'faqZhCn'
      case 'zh-TW':
        return 'faqZhTw'
      case 'jp':
        return 'faqJp'
      default:
        return 'faq'
    }
  }

  const { data, isPending } = useFaqDataQuery(getFaqContentLang(lang), selectedCategory)
  const faqs = useMemo(() => data?.faqs ?? [], [data])

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
    GA_EVENTS.BUTTON_CLICK(`faq_category_${category}`)
  }

  // 쿼리파라미터로 초기 카테고리 설정
  useEffect(() => {
    const categoryParam = searchParams.get('category') || undefined
    if (categoryParam && FAQ_CATEGORIES.includes(categoryParam) && categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // 선택된 카테고리를 URL 쿼리에 반영
  useEffect(() => {
    const currentParam = searchParams.get('category') || undefined
    if (!selectedCategory) return
    if (currentParam === selectedCategory) return

    const params = new URLSearchParams(searchParams.toString())
    params.set('category', selectedCategory)
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory])

  return (
    <section className='pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+80px)]'>
      <div className="sm:hidden">
        <HeaderWithBackButton />
      </div>
      <div className='flex flex-col gap-[8px] sm:gap-[20px] items-center mb-[35px] pt-[7px] sm:mt-[128px]'>
        <h2 className='text-[24px] font-bold text-text-main-lv1 leading-[29px] sm:text-[50px] sm:leading-[60px]'>{t('need_help')}</h2>
        <p className='text-[14px] text-text-main-lv3 leading-[17px] sm:text-[20px] sm:leading-[24px]'>{t('select_category')}</p>
      </div>
      <div className="sm:flex sm:gap-[70px] sm:mt-[70px] mx-auto px-5 sm:max-w-fit sm:flex-col">
        <div className="w-full">
          <div className="flex flex-wrap gap-2 items-center justify-center">
            {FAQ_CATEGORIES.map((category, index) => (
              <Button
                key={index}
                size='small'
                btnColor={selectedCategory === category ? 'primary' : 'secondary'}
                onClick={() => handleCategoryClick(category)}
                className='text-[13px] font-medium px-[12px]'>
                {t(category)}
              </Button>
            ))}
          </div>
        </div>
        <div className="sm:flex-1 flex flex-col sm:mt-0 mt-[38px]">
          <div className='bg-surface-lv1 px-[16px] py-[8px] rounded-[20px]'>
            {isPending ? null : <ul>
              {faqs.length > 0 ? (
                faqs.map((faq) => (
                  <FaqItem key={faq.id} faq={faq} selectedCategory={selectedCategory} />
                ))
              ) : (
                <li className="py-4 font-pretendard-regular text-[15px] text-main-light-2 dark:text-main-dark-2">
                  {t('faq_text_2')}
                </li>
              )}
            </ul>}
          </div>
        </div>
        <ContactCard />
      </div>
    </section >

  )
}

export default Faq;
